import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance, directApiInstance } from "../api/httpClient";
import { toaster } from "baseui/toast";
import { BOOKING_HAS_PAYMENT_MESSAGE, isBookingHasPayment } from '../utils/apiErrors';

export interface Booking {
  id: string;
  service_id: string;
  business_id: string;
  main_user_id: number;
  reservation_start: string;
  reservation_end: string;
  status_id: number;
  special_requests?: string;
  confirmation_code?: string;
  notes?: string;
  channel?: string;
  /** Legacy boolean. `payment_status` is the real answer — see below. */
  payment?: boolean;
  booking_options?: any;

  // --- Receipt (SAM-193 / SAM-195 / SAM-197) -------------------------------
  // All of these come back on /crud/booking already; the admin simply never read them.
  /**
   * The server-computed total (functions/booking/pricing.py). NOT the sum of `booking_options`,
   * which is what the CLIENT sent — those can differ, and the one that was charged is this.
   * null on bookings written before SAM-193.
   */
  amount?: number | string | null;
  currency?: string | null;
  tax?: number | string | null;
  payment_status?: string | null;
  /** `GET /bookings` only (not /crud/booking): `pending_payment` while a payment link is open (SAM-1186). */
  payment_link_status?: string | null;
  payment_gateway?: string | null;
  payment_reference?: string | null;
  payment_auth_code?: string | null;
  /** Which role's rate was applied (SAM-195) — the answer to "why did they pay this". */
  role_used?: string | number | null;
  /** The immutable per-option breakdown priced at purchase time. */
  order_breakdown?: any;
  /**
   * SAM-197 — why a cancellation was free or penalised, as evaluated when it happened.
   * null means not evaluated, which is not the same as "no penalty".
   */
  cancellation?: {
    evaluated_at?: string;
    threshold_minutes?: number | null;
    minutes_before?: number | null;
    within_policy?: boolean;
    penalty_applies?: boolean;
  } | null;
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
  // Joined fields
  service_name?: string;
  business_name?: string;
  user_name?: string;
  user_email?: string;
}

/**
 * sam_core.booking_status — the single definition for the whole app.
 *
 *     1 pending | 2 confirmed | 3 completed | 4 cancelled | 5 no_show
 *
 * There were FOUR copies of this: here, the bookings list, the detail panel's label map, and the
 * detail panel's status <select>. Three of them had 3 and 4 the other way round, and the select
 * WROTE the id — so an admin choosing "Cancelled" saved 3, which is completed.
 *
 * Correcting them one at a time is how three of the four stayed wrong. Everything now reads from
 * here, and a test fails if the literal mapping reappears anywhere else.
 *
 * Not to be confused with business_access_status (BusinessDetailPanel, EventDashboard), which is a
 * different set ending in 4 = Expired.
 */
export const BOOKING_STATUS = [
  { id: 1, label: 'Pending', dotClass: 'bk-dot-pending' },
  { id: 2, label: 'Confirmed', dotClass: 'bk-dot-confirmed' },
  { id: 3, label: 'Completed', dotClass: 'bk-dot-completed' },
  { id: 4, label: 'Cancelled', dotClass: 'bk-dot-cancelled' },
  { id: 5, label: 'No Show', dotClass: 'bk-dot-no-show' },
] as const;

export const BOOKING_STATUS_PENDING = 1;
export const BOOKING_STATUS_CONFIRMED = 2;
export const BOOKING_STATUS_COMPLETED = 3;
export const BOOKING_STATUS_CANCELLED = 4;
export const BOOKING_STATUS_NO_SHOW = 5;

const STATUS_MAP: Record<number, string> = Object.fromEntries(
  BOOKING_STATUS.map((s) => [s.id, s.label]),
);

export const getStatusDotClass = (statusId: number): string =>
  BOOKING_STATUS.find((s) => s.id === statusId)?.dotClass || '';

export const getStatusLabel = (statusId: number): string => STATUS_MAP[statusId] || 'Unknown';

const BOOKING_EXPAND = 'user,main_user_id,service,service_id,business,business_id';

// Fetch bookings for a specific business
export const useBookings = (businessId: string | null) => {
  return useQuery<Booking[], Error>({
    queryKey: ['bookings', businessId],
    queryFn: async () => {
      if (!businessId) throw new Error('Business ID is required');
      const response = await defaultInstance({
        url: '/booking',
        method: 'GET',
        params: {
          business_id: businessId,
          order_by: 'reservation_start',
          order_direction: 'DESC',
          expand: BOOKING_EXPAND,
        }
      });
      const apiResponse = response.data;
      const rawArray: any[] = Array.isArray(apiResponse)
        ? apiResponse
        : Array.isArray((apiResponse as any).data) ? (apiResponse as any).data : [];
      return rawArray.map((item: any) => ({
        ...item,
        user_name: item.user
          ? `${item.user.name || ''} ${item.user.last_name || ''}`.trim() || undefined
          : undefined,
        user_email: item.user?.email,
        service_name: item.service?.name_en || item.service?.name_es || item.service?.name,
        business_name: item.business?.name,
      }));
    },
    enabled: !!businessId
  });
};

// Fetch ALL bookings (admin)
export const useAllBookings = () => {
  return useQuery<Booking[], Error>({
    queryKey: ['bookings', 'all'],
    queryFn: async () => {
      const response = await defaultInstance({
        url: '/booking',
        method: 'GET',
        params: {
          order_by: 'reservation_start',
          order_direction: 'DESC',
          expand: BOOKING_EXPAND,
        }
      });
      const apiResponse = response.data;
      const rawArray: any[] = Array.isArray(apiResponse)
        ? apiResponse
        : Array.isArray((apiResponse as any).data) ? (apiResponse as any).data : [];
      return rawArray.map((item: any) => ({
        ...item,
        user_name: item.user
          ? `${item.user.name || ''} ${item.user.last_name || ''}`.trim() || undefined
          : undefined,
        user_email: item.user?.email,
        service_name: item.service?.name_en || item.service?.name_es || item.service?.name,
        business_name: item.business?.name,
      }));
    },
  });
};

/**
 * SAM-608 — one booking by id, for the Bookings console deep link (?booking=<id>) when that
 * booking is not in the list already loaded (another business, or an older page).
 */
export const useBookingById = (bookingId: string | null, enabled = true) => {
  return useQuery<Booking | null, Error>({
    queryKey: ['bookings', 'by-id', bookingId],
    queryFn: async () => {
      const response = await defaultInstance({
        url: '/booking',
        method: 'GET',
        params: { id: bookingId, expand: BOOKING_EXPAND },
      });
      const apiResponse = response.data as any;
      const rawArray: any[] = Array.isArray(apiResponse)
        ? apiResponse
        : Array.isArray(apiResponse?.data) ? apiResponse.data : [];
      const item = rawArray.find((x) => String(x?.id) === String(bookingId));
      if (!item) return null;
      return {
        ...item,
        user_name: item.user
          ? `${item.user.name || ''} ${item.user.last_name || ''}`.trim() || undefined
          : undefined,
        user_email: item.user?.email,
        service_name: item.service?.name_en || item.service?.name_es || item.service?.name,
        business_name: item.business?.name,
      };
    },
    enabled: !!bookingId && enabled,
    staleTime: 30000,
  });
};

// Update booking (change status, notes, etc.)
export const useUpdateBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Booking> }) => {
      return await defaultInstance({
        url: `/booking/${id}`,
        method: 'PATCH',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toaster.positive('Booking updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to update booking', { autoHideDuration: 3000 });
    },
  });
};

// Cancel booking
export const useCancelBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      /**
       * DELETE /bookings/{id} on the booking lambda, not a PATCH straight into the row.
       *
       * Writing status_id here did three things wrong at once: it wrote 3 (completed), it never
       * released the slot the booking held in the booking engine — so the capacity stayed taken
       * for a booking nobody was coming to — and it never evaluated the service's cancellation
       * policy, so nothing recorded whether the guest cancelled in time (SAM-197).
       *
       * The endpoint does all three. `directApiInstance` because it is not a /crud route.
       */
      return await directApiInstance({
        url: `/bookings/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toaster.positive('Booking cancelled', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(isBookingHasPayment(error) ? BOOKING_HAS_PAYMENT_MESSAGE : error?.message || 'Failed to cancel booking', { autoHideDuration: isBookingHasPayment(error) ? 6000 : 3000 });
    },
  });
};
